import { useState } from "react";
import { BellRing, Check, Pencil, Receipt, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuthedSrc } from "@/lib/authedImage";
import { fmtDate, money } from "@/lib/format";
import { Confirm, FormDialog } from "@/components/kids/kidsUi";

const when = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function PaidDialog({ onClose, onSave }) {
  const [note, setNote] = useState("");
  return (
    <FormDialog open onClose={onClose} title="Mark your share paid" description="Add how you paid, if you like. The Queue doesn't move money." testid="i-paid-dialog">
      <form onSubmit={(e) => { e.preventDefault(); onSave(note); }} className="space-y-3">
        <input className="q-input min-h-[44px] w-full" maxLength={60} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Venmo, Zelle, cash…" data-testid="i-paid-note-input" />
        <button className="q-btn q-btn-primary min-h-[44px] w-full" data-testid="i-paid-save-btn">I paid</button>
      </form>
    </FormDialog>
  );
}

function ShareRow({ s, x, act, ro }) {
  const status = x.is_payer ? "Paid it" : x.status === "paid" ? "Paid" : "Open";
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2" data-testid={`share-${s.id}-${x.key}`}>
      <span className="min-w-0 flex-1 text-sm text-q-text">{x.name} <span className="tabular-nums text-q-muted">· {money(x.amount)}{x.pct != null ? ` (${x.pct}%)` : ""}</span>
        {x.status === "paid" && <span className="block text-xs text-q-muted" data-testid={`share-paid-info-${s.id}-${x.key}`}>Marked paid by {x.paid_by_name} · {when(x.paid_at)}{x.method_note ? ` · ${x.method_note}` : ""}</span>}
      </span>
      <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${x.status === "open" ? "bg-amber-400/15 text-amber-300" : "bg-emerald-400/15 text-emerald-300"}`} data-testid={`share-status-${s.id}-${x.key}`}>{status}</span>
      <span className="flex flex-wrap gap-1">
        {x.can_mark_paid && <button className="q-btn q-btn-primary min-h-[44px]" disabled={ro} onClick={() => act("paid", x)} data-testid={`share-i-paid-${s.id}-${x.key}`}><Check className="h-4 w-4" /> I paid</button>}
        {x.can_got_it && <button className="q-btn q-btn-secondary min-h-[44px]" disabled={ro} onClick={() => act("got-it", x)} data-testid={`share-got-it-${s.id}-${x.key}`}>Got it</button>}
        {x.can_not_received && <button className="q-btn q-btn-ghost min-h-[44px]" disabled={ro} onClick={() => act("not-received", x)} data-testid={`share-not-received-${s.id}-${x.key}`}><RotateCcw className="h-4 w-4" /> Not received</button>}
        {s.payer_key === x.key ? null : x.status === "open" && (x.can_remind || x.reminder_recent) && (
          <button className="q-btn q-btn-ghost min-h-[44px]" disabled={ro || !x.can_remind} onClick={() => act("remind", x)} data-testid={`share-remind-${s.id}-${x.key}`}>
            <BellRing className="h-4 w-4" /> {x.reminder_recent ? `Reminder sent ${when(x.reminded_at)}` : "Send reminder"}
          </button>
        )}
      </span>
    </li>
  );
}

function ReceiptThumb({ hubId, id }) {
  const src = useAuthedSrc(`/organizations/${hubId}/splits/${id}/receipt`);
  return src ? <a href={src} target="_blank" rel="noreferrer" className="mt-2 inline-block" data-testid={`split-receipt-${id}`}><img src={src} alt="Receipt" className="h-20 w-20 rounded-lg object-cover" /></a> : null;
}

export default function SplitCard({ hubId, s, me, ro, onEdit, onChanged }) {
  const [paying, setPaying] = useState(null);
  const [del, setDel] = useState(false);
  const post = (path, body) => api.post(`/organizations/${hubId}/splits/${s.id}/${path}`, body).then(() => onChanged()).catch((e) => toast.error(errMsg(e)));
  const act = (kind, x) => {
    const p = `shares/${encodeURIComponent(x.key)}/${kind}`;
    if (kind === "paid") return setPaying(x);
    return post(p, {}).then(() => kind === "remind" && toast.success("Reminder sent"));
  };
  const remove = () => api.delete(`/organizations/${hubId}/splits/${s.id}`, { params: s.any_paid ? { confirm: true } : {} }).then(() => { toast.success("Split deleted"); setDel(false); onChanged(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <li className="q-card p-4" data-testid={`split-${s.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-display text-lg font-bold text-q-text" data-testid={`split-title-${s.id}`}>{s.title}</p>
          <p className="text-sm text-q-muted">{money(s.total)} · {fmtDate(s.date)} · paid by {s.payer_key === me ? "you" : s.payer_name} · {{ equal: "Equal", exact: "Exact amounts", percent: "Percentages" }[s.method]}</p>
          {s.note && <p className="mt-1 text-sm text-q-text">{s.note}</p>}
        </div>
        {s.can_edit && (
          <span className="flex gap-1">
            <button className="q-btn q-btn-ghost min-h-[44px] min-w-[44px]" aria-label="Edit split" disabled={ro} onClick={onEdit} data-testid={`split-edit-${s.id}`}><Pencil className="h-4 w-4" /></button>
            <button className="q-btn q-btn-ghost min-h-[44px] min-w-[44px] hover:text-red-300" aria-label="Delete split" disabled={ro} onClick={() => setDel(true)} data-testid={`split-delete-${s.id}`}><Trash2 className="h-4 w-4" /></button>
          </span>
        )}
      </div>
      <ul className="mt-2 divide-y divide-q-line">{s.shares.map((x) => <ShareRow key={x.key} s={s} x={x} act={act} ro={ro} />)}</ul>
      {s.has_receipt && <><Receipt className="mr-1 inline h-3 w-3 text-q-faint" /><ReceiptThumb hubId={hubId} id={s.id} /></>}
      {paying && <PaidDialog onClose={() => setPaying(null)} onSave={(note) => post(`shares/${encodeURIComponent(paying.key)}/paid`, { note }).then(() => setPaying(null))} />}
      <Confirm open={del} onClose={() => setDel(false)} title={s.any_paid ? "Some shares are already paid. Delete anyway?" : `Delete “${s.title}”?`} body="It's removed for everyone in the split." onConfirm={remove} testid={`split-delete-confirm-${s.id}`} />
    </li>
  );
}
